import * as React from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  MapPin,
  Star,
  Share2,
  CheckCircle2,
  Wifi,
  ParkingCircle,
  Coffee,
  Snowflake,
  ShieldCheck,
  Music2,
  Users,
  Clock,
  Sparkles,
  Building2,
  CalendarCheck,
  ChevronLeft,
  ChevronRight,
  Link2,
  Facebook,
  Twitter,
  Check,
} from "lucide-react";

import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

import cover from "@/assets/company-cover.webp";
import logo from "@/assets/company-logo.webp";
import s1 from "@/assets/space-1.webp";
import s2 from "@/assets/space-2.webp";
import s3 from "@/assets/space-3.webp";
import s4 from "@/assets/space-4.webp";
import s5 from "@/assets/space-5.webp";
import { Riyal } from "@/components/ui/riyal";

import { getCompany } from "@/lib/companies-data";

const ORIGIN = "https://spayzo.lovable.app";

export const Route = createFileRoute("/companies/$companyId")({
  component: CompanyProfilePage,
  head: ({ params }) => {
    const company = getCompany(params.companyId);
    const url = `${ORIGIN}/companies/${params.companyId}`;
    return {
      meta: [
        { title: `${company.name} — Company Profile · Spayzo` },
        {
          name: "description",
          content: company.description,
        },
        { property: "og:title", content: `${company.name} on Spayzo` },
        { property: "og:description", content: company.tagline },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { property: "og:image", content: `${ORIGIN}${company.cover}` },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: `${ORIGIN}${company.cover}` },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
});

type Space = {
  id: string;
  name: string;
  image: string;
  price: number;
  location: string;
  tags: string[];
  rating: number;
  reviews: number;
  featured?: boolean;
};

const BASE_SPACES: Space[] = [
  {
    id: "1",
    name: "Industrial loft with brick walls",
    image: s1,
    price: 64,
    location: "Williamsburg, Brooklyn",
    tags: ["Studio", "Events"],
    rating: 4.92,
    reviews: 128,
    featured: true,
  },
  {
    id: "2",
    name: "Sunlit photography studio",
    image: s2,
    price: 48,
    location: "DUMBO, Brooklyn",
    tags: ["Photo", "Studio"],
    rating: 4.86,
    reviews: 92,
    featured: true,
  },
  {
    id: "3",
    name: "Minimal podcast room",
    image: s3,
    price: 32,
    location: "Greenpoint, Brooklyn",
    tags: ["Podcast", "Office"],
    rating: 4.78,
    reviews: 64,
  },
  {
    id: "4",
    name: "Open creative gallery",
    image: s4,
    price: 88,
    location: "Bushwick, Brooklyn",
    tags: ["Gallery", "Events"],
    rating: 4.95,
    reviews: 41,
  },
  {
    id: "5",
    name: "Rooftop lounge with skyline view",
    image: s5,
    price: 120,
    location: "Long Island City, Queens",
    tags: ["Rooftop", "Events"],
    rating: 4.89,
    reviews: 73,
  },
  {
    id: "6",
    name: "Warm meeting room",
    image: s2,
    price: 28,
    location: "Williamsburg, Brooklyn",
    tags: ["Office", "Meeting"],
    rating: 4.7,
    reviews: 38,
  },
];

// Generate extra spaces to demonstrate pagination
const SPACES: Space[] = Array.from({ length: 18 }).map((_, i) => {
  const base = BASE_SPACES[i % BASE_SPACES.length];
  return { ...base, id: `${i + 1}` };
});

const AMENITIES = [
  { icon: Wifi, label: "Gigabit Wi-Fi" },
  { icon: ParkingCircle, label: "On-site parking" },
  { icon: Coffee, label: "Coffee & catering" },
  { icon: Snowflake, label: "Climate control" },
  { icon: Music2, label: "Pro sound systems" },
  { icon: ShieldCheck, label: "Verified hosts" },
  { icon: Users, label: "Up to 80 guests" },
  { icon: Clock, label: "Flexible hours" },
];

const POLICIES = [
  "Cancel free up to 48 hours before your booking",
  "Quiet hours after 10 PM in residential locations",
  "No smoking indoors — designated outdoor areas available",
  "Damage deposit may apply for events over 30 guests",
];

const BASE_REVIEWS = [
  {
    name: "Maya Rodriguez",
    initials: "MR",
    rating: 5,
    date: "March 2026",
    text: "Hosted a product launch at the brick loft — flawless. The team was responsive, the lighting was exactly as advertised, and check-in took 30 seconds.",
  },
  {
    name: "Daniel Park",
    initials: "DP",
    rating: 5,
    date: "February 2026",
    text: "Used the photo studio twice this quarter. Clean, well-maintained, and the add-on equipment saved us a separate rental.",
  },
  {
    name: "Aisha Khan",
    initials: "AK",
    rating: 4,
    date: "January 2026",
    text: "Beautiful space and great hosts. Only feedback — parking filled up early on a Saturday, plan ahead.",
  },
  {
    name: "Tom Reuter",
    initials: "TR",
    rating: 5,
    date: "December 2025",
    text: "Booked the rooftop for a 40-person dinner. The view at sunset is unmatched in Brooklyn.",
  },
];

const REVIEWS = Array.from({ length: 47 }).map((_, i) => ({
  ...BASE_REVIEWS[i % BASE_REVIEWS.length],
  name: `${BASE_REVIEWS[i % BASE_REVIEWS.length].name.split(" ")[0]} ${String.fromCharCode(65 + (i % 26))}.`,
}));

type Tab = "spaces" | "about" | "reviews";

function CompanyProfilePage() {
  const [tab, setTab] = React.useState<Tab>("spaces");
  const spacesRef = React.useRef<HTMLDivElement | null>(null);

  const avgRating = REVIEWS.reduce((s, r) => s + r.rating, 0) / REVIEWS.length;

  const scrollToTabs = () => {
    setTab("spaces");
    requestAnimationFrame(() => spacesRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
  };

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />

      <main className="pb-32 lg:pb-16">
        {/* Cover */}
        <section className="relative">
          <div className="relative h-56 w-full overflow-hidden sm:h-72 md:h-96">
            <img
              src={cover}
              alt="Loftworks Studios cover"
              width={1920}
              height={768}
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/30 to-transparent" />
            <Link
              to="/discover"
              className="absolute start-4 top-4 inline-flex items-center gap-1 rounded-full bg-background/80 px-3 py-1.5 text-xs font-medium text-foreground backdrop-blur-md transition hover:bg-background sm:start-6 sm:top-6"
            >
              <ChevronLeft className="h-3.5 w-3.5" /> Discover
            </Link>
            <div className="absolute end-4 top-4 flex gap-2 sm:end-6 sm:top-6">
              <ShareMenu />
            </div>
          </div>

          {/* Identity */}
          <div className="mx-auto -mt-14 max-w-7xl px-4 sm:-mt-16 sm:px-6 lg:px-8">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
                <div className="relative h-24 w-24 shrink-0 overflow-hidden rounded-3xl border-4 border-background bg-card shadow-elevated sm:h-28 sm:w-28">
                  <img
                    src={logo}
                    alt="Loftworks logo"
                    width={512}
                    height={512}
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="pb-10">
                  <div className="flex flex-wrap items-center gap-2">
                    <h1 className="font-display text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
                      Loftworks Studios
                    </h1>
                    <span className="inline-flex items-center gap-1 rounded-full bg-[#5958F3]/10 px-2.5 py-1 text-xs font-semibold text-[#5958F3] ring-1 ring-inset ring-[#5958F3]/20">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Verified
                    </span>
                  </div>
                  <p className="mt-1 max-w-xl text-sm text-muted-foreground">
                    Brooklyn&apos;s curated collection of industrial lofts, photo studios and rooftop venues — bookable
                    by the hour.
                  </p>
                  <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1">
                      <MapPin className="h-3.5 w-3.5" /> Brooklyn, New York
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <CalendarCheck className="h-3.5 w-3.5" /> Host on Spayzo since March 2019
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button onClick={scrollToTabs} className="rounded-full shadow-glow">
                  <Building2 className="h-4 w-4" /> View spaces
                </Button>
              </div>
            </div>

            {/* Stats */}
            <dl className="mt-8 grid grid-cols-3 divide-x divide-border overflow-hidden rounded-3xl border border-border bg-card shadow-soft">
              <Stat icon={Building2} value={`${SPACES.length}`} label="Spaces" />
              <Stat icon={Star} value={avgRating.toFixed(2)} label="Avg rating" />
              <Stat icon={CalendarCheck} value="2.4k" label="Bookings" />
            </dl>
          </div>
        </section>

        {/* Sticky tabs */}
        <div
          ref={spacesRef}
          className="sticky top-16 z-30 mt-10 border-b border-border bg-background/85 backdrop-blur-xl"
        >
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <nav
              className="-mx-1 flex gap-1 overflow-x-auto scrollbar-none"
              role="tablist"
              aria-label="Company sections"
            >
              {(
                [
                  { id: "spaces", label: `Spaces · ${SPACES.length}` },
                  { id: "about", label: "About" },
                  { id: "reviews", label: `Reviews · ${REVIEWS.length}` },
                ] as { id: Tab; label: string }[]
              ).map((t) => {
                const active = tab === t.id;
                return (
                  <button
                    key={t.id}
                    role="tab"
                    aria-selected={active}
                    onClick={() => setTab(t.id)}
                    className={cn(
                      "relative whitespace-nowrap px-4 py-3 text-sm font-medium transition-colors",
                      active ? "text-foreground" : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    {t.label}
                    <span
                      className={cn(
                        "absolute inset-x-2 -bottom-px h-0.5 rounded-full transition-all",
                        active ? "bg-foreground" : "bg-transparent",
                      )}
                    />
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Tab panels */}
        <div className="mx-auto mt-8 max-w-7xl px-4 sm:px-6 lg:px-8">
          <div key={tab} className="animate-in fade-in-50 slide-in-from-bottom-1 duration-300">
            {tab === "spaces" && <SpacesPanel />}
            {tab === "about" && <AboutPanel />}
            {tab === "reviews" && <ReviewsPanel avg={avgRating} />}
          </div>
        </div>
      </main>

      {/* Mobile sticky CTA */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-card/95 p-3 backdrop-blur-xl shadow-elevated lg:hidden">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-1">
          <div className="min-w-0">
            <div className="truncate font-display text-sm font-bold">Loftworks Studios</div>
            <div className="text-xs text-muted-foreground">
              {SPACES.length} spaces · ★ {avgRating.toFixed(2)}
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={scrollToTabs} className="rounded-full px-5 shadow-glow">
              View spaces
            </Button>
          </div>
        </div>
      </div>

      <SiteFooter />
    </div>
  );
}

function Stat({
  icon: Icon,
  value,
  label,
}: {
  icon: React.ComponentType<{ className?: string }>;
  value: string;
  label: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-1 px-3 py-4 text-center sm:flex-row sm:gap-3 sm:py-5">
      <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary-soft text-primary">
        <Icon className="h-4 w-4" />
      </span>
      <div>
        <div className="font-display text-lg font-bold leading-none">{value}</div>
        <div className="mt-1 text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
      </div>
    </div>
  );
}

const SPACES_PER_PAGE = 8;

function SpacesPanel() {
  const [page, setPage] = React.useState(1);
  const totalPages = Math.ceil(SPACES.length / SPACES_PER_PAGE);
  const start = (page - 1) * SPACES_PER_PAGE;
  const items = SPACES.slice(start, start + SPACES_PER_PAGE);

  return (
    <section aria-label="Spaces">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h2 className="font-display text-xl font-semibold sm:text-2xl">Browse all spaces</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Hand-picked locations across Brooklyn and Queens — bookable by the hour.
          </p>
        </div>
        <Button asChild variant="ghost" size="sm" className="rounded-full">
          <Link to="/discover">See all</Link>
        </Button>
      </div>

      <ul className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {items.map((s, i) => (
          <li key={`${s.id}-${start + i}`}>
            <SpaceCard space={s} />
          </li>
        ))}
      </ul>

      <Pagination page={page} totalPages={totalPages} onChange={setPage} />
    </section>
  );
}

function Pagination({
  page,
  totalPages,
  onChange,
}: {
  page: number;
  totalPages: number;
  onChange: (p: number) => void;
}) {
  if (totalPages <= 1) return null;
  const pages = Array.from({ length: totalPages }, (_, i) => i + 1);
  return (
    <nav aria-label="Pagination" className="mt-10 flex items-center justify-center gap-2">
      <Button
        variant="outline"
        size="icon"
        className="h-9 w-9 rounded-full"
        onClick={() => onChange(Math.max(1, page - 1))}
        disabled={page === 1}
        aria-label="Previous page"
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>
      {pages.map((p) => (
        <button
          key={p}
          onClick={() => onChange(p)}
          aria-current={p === page ? "page" : undefined}
          className={cn(
            "h-9 min-w-9 rounded-full px-3 text-sm font-medium transition-colors",
            p === page ? "bg-foreground text-background" : "text-muted-foreground hover:bg-muted hover:text-foreground",
          )}
        >
          {p}
        </button>
      ))}
      <Button
        variant="outline"
        size="icon"
        className="h-9 w-9 rounded-full"
        onClick={() => onChange(Math.min(totalPages, page + 1))}
        disabled={page === totalPages}
        aria-label="Next page"
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    </nav>
  );
}

function ShareMenu() {
  const [open, setOpen] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  const url = typeof window !== "undefined" ? window.location.href : "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  };

  const shareFacebook = () => {
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  const shareTwitter = () => {
    window.open(
      `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent("Check out Loftworks Studios on Spayzo")}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          size="icon"
          variant="outline"
          aria-label="Share"
          className={cn(
            "h-9 w-9 rounded-full bg-background/80 backdrop-blur-md transition-all",
            open && "ring-2 ring-primary ring-offset-2 ring-offset-background",
          )}
        >
          <Share2 className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 rounded-2xl p-2">
        <button
          onClick={copyLink}
          className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm transition-colors hover:bg-muted"
        >
          {copied ? <Check className="h-4 w-4 text-primary" /> : <Link2 className="h-4 w-4" />}
          {copied ? "Link copied" : "Copy link"}
        </button>
        <button
          onClick={shareFacebook}
          className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm transition-colors hover:bg-muted"
        >
          <Facebook className="h-4 w-4" />
          Share on Facebook
        </button>
        <button
          onClick={shareTwitter}
          className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm transition-colors hover:bg-muted"
        >
          <Twitter className="h-4 w-4" />
          Share on X (Twitter)
        </button>
      </PopoverContent>
    </Popover>
  );
}

function SpaceCard({ space }: { space: Space }) {
  return (
    <Link
      to="/spaces/$spaceId"
      params={{ spaceId: space.id }}
      className="group relative block overflow-hidden rounded-3xl border border-border bg-card shadow-soft transition-all hover:-translate-y-0.5 hover:shadow-elevated"
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={space.image}
          alt={space.name}
          loading="lazy"
                decoding="async"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        {space.featured && (
          <span className="absolute start-3 top-3 inline-flex items-center gap-1 rounded-full bg-background/90 px-2.5 py-1 text-[11px] font-semibold text-foreground backdrop-blur">
            <Sparkles className="h-3 w-3 text-primary" /> Featured
          </span>
        )}
        <span className="absolute end-3 top-3 inline-flex items-center gap-1 rounded-full bg-background/90 px-2.5 py-1 text-[11px] font-semibold backdrop-blur">
          <Star className="h-3 w-3 fill-foreground text-foreground" />
          {space.rating}
        </span>
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <h3 className="line-clamp-1 font-display text-base font-semibold">{space.name}</h3>
          <div className="text-end">
            <div className="font-display text-base font-semibold leading-none"><Riyal /> {space.price}</div>
            <div className="text-[11px] text-muted-foreground">/hour</div>
          </div>
        </div>
        <div className="mt-1.5 inline-flex items-center gap-1 text-xs text-muted-foreground">
          <MapPin className="h-3 w-3" /> {space.location}
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {space.tags.map((t) => (
            <span key={t} className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
              {t}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
}

function AboutPanel() {
  return (
    <section aria-label="About" className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_320px]">
      <div className="space-y-10">
        <div>
          <h2 className="font-display text-xl font-semibold sm:text-2xl">About Loftworks</h2>
          <div className="mt-3 space-y-4 text-base leading-relaxed text-muted-foreground">
            <p>
              Founded in 2019 in a single Williamsburg warehouse, Loftworks Studios has grown into Brooklyn&apos;s most
              trusted network of hourly creative spaces. Our team curates every venue for natural light, quiet streets
              and easy load-in.
            </p>
            <p>
              We work with photographers, founders, content teams and event hosts who need a space that feels considered
              — not corporate. Every booking includes a dedicated space manager, fast Wi-Fi, and a transparent flat
              hourly rate.
            </p>
          </div>
        </div>

        <div>
          <h3 className="font-display text-lg font-semibold">Amenities & services</h3>
          <ul className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-2">
            {AMENITIES.map((a) => (
              <li
                key={a.label}
                className="flex items-center gap-3 rounded-2xl border border-border bg-card p-3 text-sm shadow-soft"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary-soft text-primary">
                  <a.icon className="h-4 w-4" />
                </span>
                {a.label}
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="font-display text-lg font-semibold">Policies</h3>
          <ul className="mt-4 space-y-2.5">
            {POLICIES.map((p) => (
              <li key={p} className="flex items-start gap-3 text-sm text-muted-foreground">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                {p}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <aside className="space-y-3">
        <div className="rounded-3xl border border-dashed border-border p-5 text-sm text-muted-foreground">
          <div className="font-display text-sm font-semibold text-foreground">Response time</div>
          <p className="mt-1">Within 1 hour · 7 days a week</p>
          <div className="mt-3 font-display text-sm font-semibold text-foreground">Languages</div>
          <p className="mt-1">English · Spanish</p>
        </div>
      </aside>
    </section>
  );
}

const REVIEWS_PER_PAGE = 20;

function ReviewsPanel({ avg }: { avg: number }) {
  const [page, setPage] = React.useState(1);
  const totalPages = Math.ceil(REVIEWS.length / REVIEWS_PER_PAGE);
  const start = (page - 1) * REVIEWS_PER_PAGE;
  const items = REVIEWS.slice(start, start + REVIEWS_PER_PAGE);

  return (
    <section aria-label="Reviews" className="grid gap-10 lg:grid-cols-[280px_minmax(0,1fr)]">
      <aside className="lg:sticky lg:top-36 lg:self-start">
        <div className="rounded-3xl border border-border bg-card p-6 text-center shadow-soft">
          <div className="font-display text-5xl font-bold">{avg.toFixed(2)}</div>
          <div className="mt-2 inline-flex items-center gap-0.5 text-primary">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star key={i} className={cn("h-4 w-4", i < Math.round(avg) ? "fill-current" : "opacity-30")} />
            ))}
          </div>
          <div className="mt-2 text-sm text-muted-foreground">Based on {REVIEWS.length} reviews</div>
        </div>
      </aside>

      <div>
        <ul className="space-y-4">
          {items.map((r, i) => (
            <li
              key={`${r.name}-${start + i}`}
              className="rounded-3xl border border-border bg-card p-5 shadow-soft transition-colors hover:border-primary/40"
            >
              <div className="flex items-start gap-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary-soft text-sm font-semibold text-primary">
                  {r.initials}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="font-display text-sm font-semibold">{r.name}</span>
                    <span className="text-xs text-muted-foreground">{r.date}</span>
                  </div>
                  <div className="mt-1 inline-flex items-center gap-0.5 text-primary">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} className={cn("h-3.5 w-3.5", i < r.rating ? "fill-current" : "opacity-30")} />
                    ))}
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{r.text}</p>
                </div>
              </div>
            </li>
          ))}
        </ul>
        <Pagination page={page} totalPages={totalPages} onChange={setPage} />
      </div>
    </section>
  );
}
